import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs'; 
import { HttpClient } from '@angular/common/http';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';

import { UrlService } from '@core/services/url/url.service';
import { FileMetadata, FileUploadInterface, CompletedPart, PresignedUrlResponse } from '@core/objects/storage';
import { FileUploadQueue } from './file-upload-queue.class';
import { StorageFileSizePipe } from '@core/pipes/storage-file-size.pipes';

@Component({
  selector: 'core-storage-files-upload',
  templateUrl: './storage-files-upload.component.html',
  styleUrls: ['./storage-files-upload.component.scss']
})
export class CoreStorageFilesUploadComponent implements OnInit, OnChanges {
  protected readonly PLUGIN_SHARE_FEEDBACK_CONTEXT = 'plugin-share-feedback';
  protected readonly PLUGIN_SHARE_UPLOAD_VERSION_CONTEXT = 'plugin-share-upload-version';
  protected readonly MESSAGE_SHARE_FEEDBACK_CONTEXT = 'message-share-feedback';
  // Api to buid urls for storage
  private STORAGE_API = {
    POST_CREATE_UPLOAD: '/app/t',
    PUT_COMPLETE_UPLOAD: (uuid: string) => `/app/t/${uuid}`,
    GET_METADATA: (uuid: string) => `/app/f/${uuid}/m`,
  };

  private AMS_API = { GET_LEGACY_TEMP_ID: (uuid: string) => `/files/t/${uuid}`} as const;

  @Output() response = new EventEmitter<FileMetadata[]>();
  @Output() cancelItem = new EventEmitter<any>();
  @Output() onUploading = new EventEmitter<boolean>();
  @Input() multiple: boolean = true;
  @Input() context: string = 'default'; // context for upload (e.g., 'feedback', 'version-upload', etc.)
  @Input() hiddenUploaded: boolean = false;
  @Input() removed: FileMetadata | null = null;
  @Input() contextUrl: string = ''; // Optional context URL for special upload contexts
  @Input() legacyTempId: boolean = false; // Flag used for get legacy numeric id before emit file event.
  @Input() allowedTypes?: string[]; // Optional list of allowed MIME types (e.g., ['image/png', 'application/pdf'])
  public fileInputName: string;
  public files: FileUploadQueue;
  public hasBaseDropZoneOver = false;
  @Output() fileRejected = new EventEmitter<{ filename: string; reason: string }>();

  private readonly CHUNK_SIZE = 10 * 1024 * 1024; // 10MB (ajustable)

  constructor(
    private urlSrv: UrlService,
    private httpSrv: HttpClient
  ) {
    this.files = new FileUploadQueue();
    this.fileInputName = Math.random().toString(36).substring(7);
  }

  ngOnInit() {
    this.constructUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['removed'] && this.removed) {
      const fileToRemove = this.files.queue.find(f => f.metadata?.uuid === this.removed?.uuid);
      if (fileToRemove) this.removeFile(fileToRemove);
    }
  }

  private constructUrl() {
    // Use proper context URL if both provided
    if ((this.context === this.PLUGIN_SHARE_UPLOAD_VERSION_CONTEXT ||
          this.context === this.PLUGIN_SHARE_FEEDBACK_CONTEXT
    ) && this.contextUrl != '') {
      this.STORAGE_API = {
        POST_CREATE_UPLOAD: `${this.contextUrl}/t`,
        PUT_COMPLETE_UPLOAD: (uuid: string) => `${this.contextUrl}/t/${uuid}`,
        GET_METADATA: (uuid: string) => `${this.contextUrl}/f/${uuid}/m`,
      };
    // if (this.context === 'feedback' && this.contextUrl) {
      // maybe use here for future features
    }
  }

  // === DROP ZONE ===
  public dropped(entries: NgxFileDropEntry[]) {
    for (const entry of entries) {
      if (entry.fileEntry.isFile) {
        const fileEntry = entry.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (this.isFileTypeAllowed(file)) {
            this.addToFiles(file);
          } else {
            this.fileRejected.emit({ filename: file.name, reason: 'File type not allowed' });
          }
        });
      }
    }
  }

  public fileOver(event: any) { }
  public fileLeave(event: any) { }

  // === VALIDATE FILE TYPE according type list ===
  private isFileTypeAllowed(file: File): boolean {
    // If no restrictions, allow all
    if (!this.allowedTypes || this.allowedTypes.length === 0) {
      return true;
    }
    
    // Check if file MIME type is in allowedTypes
    return this.allowedTypes.includes(file.type);
  }

  // === ADD FILE ===
  protected addToFiles(file: File) {
    const f = this.files.create(file);
    if (f) {
      this.startS3Upload(f);
    }
  }

  // === UPLOAD TO S3 WITH PRE-SIGNED URLS ===
  protected async startS3Upload(f: FileUploadInterface) {
    this.files.setStatus(f, FileUploadQueue.PENDING);
    this.onUploadingEmit();

    try {
      // 1. Request pre-signed URLs
      const payload = {
        filename: f.file.name,
        size: f.file.size,
      };

      const presigned: PresignedUrlResponse = await firstValueFrom(
        this.httpSrv.post<PresignedUrlResponse>(
          this.urlSrv.buildFiles(this.STORAGE_API.POST_CREATE_UPLOAD),
          payload,
          { withCredentials: true }
        )
      );

      f.uuid = presigned.uuid;

      // 2. Upload each part
      const uploadPromises: Promise<CompletedPart>[] = [];
      let uploadedBytes = 0;

      for (const part of presigned.parts) {
        const chunk = f.file.slice(part.start_byte, part.end_byte + 1);
        const promise = this.uploadPart(chunk, part.url, part.part_number, f.file.type)
          .then(etags => {
            uploadedBytes += chunk.size;
            const progress = (uploadedBytes / f.file.size) * 100;
            this.files.setProgress(f, progress);
            return etags;
          });
        uploadPromises.push(promise);
      }

      const completedParts = await Promise.all(uploadPromises);

      // 3. Complete the upload
      await firstValueFrom(
        this.httpSrv.put(
          this.urlSrv.buildFiles(this.STORAGE_API.PUT_COMPLETE_UPLOAD(f.uuid!)),
          { parts: completedParts },
          { withCredentials: true }
        )
      );

      // 4. Get metadata
      let finalFile: FileMetadata = await firstValueFrom(
        this.httpSrv.get<FileMetadata>(
          this.urlSrv.buildFiles(this.STORAGE_API.GET_METADATA(f.uuid!)),
          { withCredentials: true }
        )
      );

      // 5. Handle legacy ID if needed
      if (this.legacyTempId && f.uuid) {
        // try {
          const legacyResponse = await firstValueFrom(
            this.httpSrv.get<{ id: string }>(
              this.urlSrv.build(this.AMS_API.GET_LEGACY_TEMP_ID(f.uuid))
            )
          );

          // overwrite finalFile id with legacy id
          finalFile = {
            ...finalFile,
            id: legacyResponse.id  // ← This is legacy temporal ID
          };

      }

      this.files.setFileObject(f, finalFile);
      this.files.setStatus(f, FileUploadQueue.SUCCESS);
      this.emitUpdatedFiles();
      this.onUploadingEmit();

    } catch (error: any) {
      console.error('S3 Upload failed:', error);
      this.files.setStatus(f, FileUploadQueue.ERROR);
      this.onUploadingEmit();
    }
  }

  private async uploadPart(
    chunk: Blob,
    url: string,
    partNumber: number,
    contentType: string
  ): Promise<CompletedPart> {
    const response = await fetch(url, {
      method: 'PUT',
      body: chunk,
      headers: {
        'Content-Type': contentType
      }
    });

    if (!response.ok) {
      throw new Error(`Part ${partNumber} failed: ${response.status}`);
    }

    const etag = response.headers.get('ETag')?.replace(/"/g, '') || '';
    return { token: etag, part_number: partNumber };
  }

  // === EVENTS ===
  protected emitUpdatedFiles() {
    const files = this.files.getOnSuccess().map(f => f.metadata).filter(Boolean) as FileMetadata[];
    this.response.emit(files);
  }

  protected onUploadingEmit() {
    const pending = this.files.getOnPending().length > 0;
    this.onUploading.emit(pending);
  }

  // === REMOVE FILE ===
  public removeFile(file: FileUploadInterface) {
    if (file.status === FileUploadQueue.PENDING) {
      // we can halt proccess with AbortController if implemented
    }

    this.files.remove(file);
    this.emitUpdatedFiles();
    this.onUploadingEmit();
  }

  public getViewName(item: FileUploadInterface): string {
    const pipe = new StorageFileSizePipe();
    return `${item.file.name} (${pipe.transform(item.file.size)})`;
  }
}
